export function normalizeSearchText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function tokenizeSearchText(value = '') {
  return normalizeSearchText(value).split(/\s+/).filter(Boolean)
}

export function extractSignificantTokens(value = '') {
  const tokens = tokenizeSearchText(value)
  const significant = tokens.filter((token) => token.length >= 3)
  return significant.length ? significant : tokens
}

export function buildTokenVariants(token = '') {
  const normalized = normalizeSearchText(token)
  if (!normalized) return []
  const variants = new Set([normalized])
  if (normalized.length > 3 && normalized.endsWith('es')) variants.add(normalized.slice(0, -2))
  if (normalized.length > 3 && normalized.endsWith('s')) variants.add(normalized.slice(0, -1))
  return Array.from(variants).filter(Boolean)
}

export function collapseRepeatedLetters(value = '') {
  return String(value || '').replace(/(.)\1+/g, '$1')
}

export function buildConsonantSignature(value = '') {
  return collapseRepeatedLetters(String(value || '').replace(/[aeiou]+/gi, ''))
}

function buildBigrams(value = '') {
  const normalized = String(value || '').replace(/\s+/g, '')
  if (normalized.length < 2) return []
  const bigrams = []
  for (let index = 0; index < normalized.length - 1; index += 1) {
    bigrams.push(normalized.slice(index, index + 2))
  }
  return bigrams
}

export function diceCoefficient(left = '', right = '') {
  const leftBigrams = buildBigrams(left)
  const rightBigrams = buildBigrams(right)
  if (!leftBigrams.length || !rightBigrams.length) return 0

  const rightCounts = new Map()
  for (const token of rightBigrams) {
    rightCounts.set(token, Number(rightCounts.get(token) || 0) + 1)
  }

  let overlap = 0
  for (const token of leftBigrams) {
    const count = Number(rightCounts.get(token) || 0)
    if (!count) continue
    overlap += 1
    rightCounts.set(token, count - 1)
  }

  return (2 * overlap) / (leftBigrams.length + rightBigrams.length)
}

export function levenshteinDistance(left = '', right = '') {
  const a = String(left || '')
  const b = String(right || '')
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length

  const matrix = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0))
  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      )
    }
  }

  return matrix[a.length][b.length]
}

export function scoreTokenMatch(queryToken = '', candidateToken = '') {
  const query = String(queryToken || '')
  const candidate = String(candidateToken || '')
  if (!query || !candidate) return 0
  if (query === candidate) return 120

  const collapsedQuery = collapseRepeatedLetters(query)
  const collapsedCandidate = collapseRepeatedLetters(candidate)
  if (collapsedQuery && collapsedQuery === collapsedCandidate) return 110

  if (
    query.length >= 3 &&
    candidate.length >= 3 &&
    (candidate.includes(query) || query.includes(candidate))
  ) {
    return 80
  }

  if (
    collapsedQuery.length >= 3 &&
    collapsedCandidate.length >= 3 &&
    (collapsedCandidate.includes(collapsedQuery) || collapsedQuery.includes(collapsedCandidate))
  ) {
    return 75
  }

  const querySignature = buildConsonantSignature(query)
  const candidateSignature = buildConsonantSignature(candidate)
  if (
    querySignature.length >= 3 &&
    candidateSignature.length >= 3 &&
    querySignature === candidateSignature &&
    query[0] === candidate[0]
  ) {
    return 68
  }

  if (query.length < 5 || candidate.length < 5) return 0

  const distance = levenshteinDistance(query, candidate)
  const maxLength = Math.max(query.length, candidate.length)
  if (distance <= 2 || distance / maxLength <= 0.25) {
    return Math.max(35, 75 - distance * 15)
  }

  const collapsedDistance = levenshteinDistance(collapsedQuery, collapsedCandidate)
  const collapsedMaxLength = Math.max(collapsedQuery.length, collapsedCandidate.length)
  if (collapsedMaxLength && (collapsedDistance <= 2 || collapsedDistance / collapsedMaxLength <= 0.25)) {
    return Math.max(40, 80 - collapsedDistance * 15)
  }

  const similarity = diceCoefficient(collapsedQuery, collapsedCandidate)
  if (similarity >= 0.72) return 62
  if (similarity >= 0.58) return 48
  if (similarity >= 0.48) return 34

  return 0
}

export function scoreTextMatch(query = '', candidate = '') {
  const normalizedQuery = normalizeSearchText(query)
  const normalizedCandidate = normalizeSearchText(candidate)
  if (!normalizedQuery || !normalizedCandidate) return 0
  if (normalizedCandidate === normalizedQuery) return 1000
  if (normalizedCandidate.includes(normalizedQuery) || normalizedQuery.includes(normalizedCandidate)) {
    return 780
  }

  const queryTokens = tokenizeSearchText(normalizedQuery)
  const candidateTokens = tokenizeSearchText(normalizedCandidate)
  let tokenScore = 0
  let matchedTokens = 0

  for (const queryToken of queryTokens) {
    let best = 0
    for (const candidateToken of candidateTokens) {
      best = Math.max(best, scoreTokenMatch(queryToken, candidateToken))
    }
    if (best > 0) {
      matchedTokens += 1
      tokenScore += best
    }
  }

  if (!matchedTokens) return 0
  return tokenScore + matchedTokens * 35 + (matchedTokens === queryTokens.length ? 70 : 0)
}

function toTextArray(value) {
  if (Array.isArray(value)) return value.map((entry) => String(entry || '')).filter(Boolean)
  if (value === null || value === undefined) return []
  const text = String(value || '')
  return text ? [text] : []
}

export function scoreEntityCandidate(query = '', candidate = {}) {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return 0

  const primaryTexts = toTextArray(candidate?.primaryText || candidate?.primaryTexts).map(normalizeSearchText)
  const aliasTexts = toTextArray(candidate?.aliases).map(normalizeSearchText)
  const secondaryTexts = toTextArray(candidate?.secondaryTexts).map(normalizeSearchText)
  const queryTokens = tokenizeSearchText(normalizedQuery)

  let score = 0

  for (const text of [...primaryTexts, ...aliasTexts]) {
    if (!text) continue
    score = Math.max(score, scoreTextMatch(normalizedQuery, text))
    const collapsedQuery = collapseRepeatedLetters(normalizedQuery)
    const collapsedText = collapseRepeatedLetters(text)
    if (collapsedQuery && collapsedText && collapsedQuery === collapsedText) {
      score = Math.max(score, 940)
    }
  }

  for (const text of secondaryTexts) {
    if (!text) continue
    // Score content mentions below the minimum title near-miss score (~168) so that
    // a note whose title closely matches the query always ranks above notes that merely
    // mention the query term in their body text.
    if (text.includes(normalizedQuery)) score = Math.max(score, 160)
  }

  const primaryTokens = [...primaryTexts, ...aliasTexts]
    .flatMap((text) => tokenizeSearchText(text))
    .filter(Boolean)

  let matchedTokens = 0
  let tokenScore = 0
  let strongMatches = 0

  for (const queryToken of queryTokens) {
    let best = 0
    for (const candidateToken of primaryTokens) {
      best = Math.max(best, scoreTokenMatch(queryToken, candidateToken))
    }
    if (!best && secondaryTexts.some((text) => text.includes(queryToken))) best = 30
    if (best > 0) {
      matchedTokens += 1
      tokenScore += best
      if (best >= 60) strongMatches += 1
    }
  }

  if (matchedTokens > 0) {
    const completionBonus =
      strongMatches === queryTokens.length ? 70 : matchedTokens === queryTokens.length ? 20 : 0
    score = Math.max(score, tokenScore + matchedTokens * 30 + completionBonus)
  }

  return score
}

export function hasSemanticTokenSignal(query = '', texts = []) {
  const queryTokens = extractSignificantTokens(query)
  if (!queryTokens.length) return false

  const normalizedTexts = (Array.isArray(texts) ? texts : [texts])
    .map((entry) => normalizeSearchText(entry))
    .filter(Boolean)

  if (!normalizedTexts.length) return false

  return queryTokens.some((token) =>
    buildTokenVariants(token).some((variant) =>
      normalizedTexts.some((text) => text && (text === variant || text.includes(variant)))
    )
  )
}

export function buildRankedMatches(items = [], query = '', options = {}) {
  const list = Array.isArray(items) ? items : []
  const limit = Math.max(1, Math.min(Number(options?.limit) || 5, 50))
  const minimumScore = Number(options?.minimumScore || 1)
  const mapTexts =
    typeof options?.mapTexts === 'function'
      ? options.mapTexts
      : (item) => ({
          primaryText: item?.name || item?.title || item?.id || '',
          aliases: item?.aliases || [],
          secondaryTexts: []
        })
  const mapResult =
    typeof options?.mapResult === 'function'
      ? options.mapResult
      : ({ item, score }) => ({
          item,
          score
        })
  const sortLabel =
    typeof options?.sortLabel === 'function'
      ? options.sortLabel
      : (item) => normalizeSearchText(item?.name || item?.title || item?.id || '')

  return list
    .map((item) => {
      const texts = mapTexts(item) || {}
      const score = scoreEntityCandidate(query, texts)
      return { item, score, texts }
    })
    .filter((entry) => entry.score >= minimumScore)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score
      return sortLabel(left.item).localeCompare(sortLabel(right.item))
    })
    .slice(0, limit)
    .map((entry) => mapResult(entry))
}

export function resolveRankedMatch(matches = [], options = {}) {
  const minScore = Number(options?.minScore || 140)
  const minGap = Number(options?.minGap || 50)
  const minSingleScore = Number(options?.minSingleScore || 120)
  const list = Array.isArray(matches) ? matches : []

  if (!list.length) {
    return { status: 'not_found', match: null, matches: [] }
  }

  if (list.length === 1) {
    return Number(list[0]?.score || 0) >= minSingleScore
      ? { status: 'single', match: list[0], matches: list }
      : { status: 'candidate', match: null, matches: list }
  }

  const bestScore = Number(list[0]?.score || 0)
  const secondScore = Number(list[1]?.score || 0)
  if (bestScore >= minScore && bestScore - secondScore >= minGap) {
    return { status: 'single', match: list[0], matches: list }
  }

  return { status: 'ambiguous', match: null, matches: list }
}

